import type {
  ParamFieldDiff,
  ParamMismatchInfo,
  WorkfrontCustomFormParameter,
  WorkfrontParameterRaw,
} from './types.js';

/**
 * Cross-workspace parameter matcher. Verbatim port of
 * `wf-audit/src/slices/workfront/components/custom-forms-migration/
 * custom-form/parameter-comparison.ts`.
 *
 * When cloning a custom form into a target workspace, every parameter
 * either:
 *  - finds an existing target parameter by name with a matching field
 *    shape (dataType / displayType / format / multiValue / refObjCode /
 *    isEncrypted + option set) → reuse the target ID, no copy needed
 *  - finds an existing target parameter by name with a *mismatched*
 *    shape → flag the mismatch for the operator (they need to rename
 *    or accept the destination's config); reuse is still legal but
 *    surfaces a warning
 *  - finds no match → create a new parameter on the target via the
 *    `copyCustomFormParams` op
 *
 * `pickParamCopyFields` is the allowlist of fields that ride the
 * create payload — strips Workfront-managed identifiers (`ID`,
 * `customerID`, etc.) so the target Workfront mints fresh ones.
 */

const HARD_COMPARE_FIELDS = [
  'dataType',
  'displayType',
  'format',
  'multiValue',
  'referenceObjCode',
  'refObjCode',
  'isEncrypted',
] as const;

function normalizeOptionSet(
  options: ReadonlyArray<{ value: string; label: string }> | undefined,
): string[] {
  return (options || []).map(({ value, label }) => `${value}::${label}`).sort();
}

function arraysEqual(a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

export function compareParameters(
  source: WorkfrontCustomFormParameter,
  dest: WorkfrontParameterRaw,
): ParamMismatchInfo | null {
  const diffs: ParamFieldDiff[] = [];

  for (const field of HARD_COMPARE_FIELDS) {
    const sourceVal = source[field];
    const destVal = dest[field];
    if (sourceVal !== destVal && !(sourceVal == null && destVal == null)) {
      diffs.push({ field, sourceValue: sourceVal, destValue: destVal });
    }
  }

  if (diffs.length > 0) {
    return {
      severity: 'hard',
      diffs,
      summary: diffs
        .map(
          (d) =>
            `${d.field}: ${String(d.sourceValue ?? 'null')} → ${String(d.destValue ?? 'null')}`,
        )
        .join('; '),
    };
  }

  const sourceOpts = normalizeOptionSet(source.parameterOptions);
  const destOpts = normalizeOptionSet(dest.parameterOptions);

  if (!arraysEqual(sourceOpts, destOpts)) {
    const optDiffs: ParamFieldDiff[] = [
      {
        field: 'parameterOptions',
        sourceValue: `${sourceOpts.length} options`,
        destValue: `${destOpts.length} options`,
      },
    ];
    return {
      severity: 'options',
      diffs: optDiffs,
      summary: `Options differ: source has ${sourceOpts.length}, dest has ${destOpts.length}`,
    };
  }

  return null;
}

export function findBestMatch(
  source: WorkfrontCustomFormParameter,
  candidates: ReadonlyArray<WorkfrontParameterRaw>,
): { match: WorkfrontParameterRaw; mismatch: ParamMismatchInfo | null } | null {
  const sameName = candidates.filter(({ name }) => name === source.name);
  if (sameName.length === 0) return null;

  // Prefer exact config match.
  for (const candidate of sameName) {
    const mismatch = compareParameters(source, candidate);
    if (!mismatch) return { match: candidate, mismatch: null };
  }

  // Fall back to first match with mismatch info.
  const first = sameName[0]!;
  return { match: first, mismatch: compareParameters(source, first) };
}

const PARAM_COPY_FIELDS = [
  'name',
  'label',
  'description',
  'dataType',
  'displayType',
  'displaySize',
  'format',
  'length',
  'referenceObjCode',
  'refObjCode',
  'multiValue',
  'required',
  'calculatedExpression',
  'validationExpression',
  'isEncrypted',
  'fieldDefinition',
  'parameterDescriptiveText',
] as const;

export function pickParamCopyFields(
  parameter: WorkfrontCustomFormParameter,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const field of PARAM_COPY_FIELDS) {
    if (parameter[field] !== undefined) {
      result[field] = parameter[field];
    }
  }
  return result;
}
